import React, { Component } from 'react';
import classNames from 'classnames';

import ListViewItemPreviewAndControls from './list-view-item-preview-and-controls';
import ItemSize from './item-size';
import resetPhotoEdits from '../../common/reset-photo-edits';
import PhotoFilename from '../../common/photo-filename';
import findRecommendedPrintSize from '../../common/photo-quality/recommended-print-size';

const baseNumberOfSizesToShow = 4;
const secondNumberOfSizesToShow = 10;
const subsequentPagesOfSizesToShow = 5;
const DEFAULT_DIMENSIONS = {
  w: 1000,
  h: 1000,
};

const printSizeListEqual = (listA, listB) =>
  listA.length === listB.length &&
  listA.every(
    (o, i) =>
      Object.keys(o).length === Object.keys(listB[i]).length &&
      o.key === listB[i].key
  );

class PhotosListItem extends Component {
  constructor(props) {
    super(props);

    this.state = {
      numberOfSizesToShow: baseNumberOfSizesToShow,
      recommendedPrintSize: findRecommendedPrintSize(
        props.sizes,
        this.rootPhoto
      ),
    };
  }

  componentDidUpdate(prevProps) {
    const { sizes: previousSizes } = prevProps;
    const { sizes } = this.props;

    // If print sizes have changed then update recommendedPrintSize so that an invalid print size is not recommended or selectable
    if (!printSizeListEqual(previousSizes, sizes)) {
      const recommendedPrintSize = findRecommendedPrintSize(
        sizes,
        this.rootPhoto
      );
      this.setState({
        // eslint-disable-line react/no-did-update-set-state
        recommendedPrintSize,
      });
    }
  }

  get rootPhoto() {
    const { photos = {} } = this.props;
    const firstSizeKey = Object.keys(photos)[0];
    return firstSizeKey ? photos[firstSizeKey][0] : null;
  }

  handleShowMoreSizesClick = () => {
    this.setState(({ numberOfSizesToShow: prevNumberOfSizesToShow }) => {
      const numberOfSizesToShow = (({ sizes }) => {
        if (prevNumberOfSizesToShow >= sizes.length) {
          return baseNumberOfSizesToShow;
        }

        if (prevNumberOfSizesToShow < secondNumberOfSizesToShow) {
          return secondNumberOfSizesToShow;
        }

        return prevNumberOfSizesToShow + subsequentPagesOfSizesToShow;
      })(this.props);

      return {
        numberOfSizesToShow,
      };
    });
  };

  rawDimensions() {
    const { photos } = this.props;

    const sizeWithRawDimensions = Object.keys(photos).find(sizeKey => {
      const size = photos[sizeKey];
      return size && size[0] && size[0].raw_dimensions;
    });

    if (sizeWithRawDimensions) {
      return photos[sizeWithRawDimensions][0].raw_dimensions;
    }

    return null;
  }

  previews() {
    const { photos } = this.props;

    return Object.entries(photos)
      .reduce((list, [, sizePhotos]) => {
        const photo = sizePhotos[0];
        if (photo && photo.quantity) {
          list.push(photo);
        }

        return list;
      }, [])
      .sort((a, b) => {
        // Return a sorted array by the id's of photo sizes so that newly added sizes
        // top the viewed collection
        // First two if (s) account for newly added images where id is null
        if (a.id === null) return -1;
        if (b.id === null) return 1;
        return a.id - b.id;
      });
  }

  render() {
    const { sizes, photos, rootId, disabled } = this.props;
    const { numberOfSizesToShow, recommendedPrintSize } = this.state;

    const previews = this.previews();
    const moreFewerButtonToggleLabel = `${
      numberOfSizesToShow >= sizes.length ? 'Fewer' : 'More'
    } sizes`;
    const rawDimensions = this.rawDimensions();
    const printSizeList = [
      recommendedPrintSize,
      ...sizes.filter(
        size => !recommendedPrintSize || size.key !== recommendedPrintSize.key
      ),
    ].filter(Boolean);

    let $previews = previews.map((photo, key, array) => {
      return (
        <ListViewItemPreviewAndControls
          // eslint-disable-next-line react/no-array-index-key
          key={key}
          photo={photo}
          isLastPhoto={array.length === 1}
          recommendedPrintSize={recommendedPrintSize}
          showSize
          showQuantity
          showLowQualityWarning
        />
      );
    });

    if (previews.length === 0) {
      resetPhotoEdits(this.rootPhoto);

      $previews = (
        <ListViewItemPreviewAndControls
          photo={this.rootPhoto}
          displayRootImageOnly
          showQuantity
        />
      );
    }

    return (
      <section className="list-item">
        <PhotoFilename photo={this.rootPhoto} />
        <div className="d-flex">
          <div className="flex-grow-1">{$previews}</div>
          <div
            className={classNames('list-item__sizes flex-shrink-0', {
              'list-item__sizes-expanded':
                numberOfSizesToShow > baseNumberOfSizesToShow,
            })}>
            <ul className="list-unstyled">
              {printSizeList.slice(0, numberOfSizesToShow).map(size => (
                <ItemSize
                  rootId={rootId}
                  {...size}
                  sizeKey={size.key}
                  photos={photos[size.key] || []}
                  showLowQualityWarning={!!rawDimensions}
                  rawDimensions={rawDimensions || DEFAULT_DIMENSIONS}
                  disabled={disabled}
                  recommendedPrintSize={recommendedPrintSize}
                />
              ))}
            </ul>
            {sizes.length > baseNumberOfSizesToShow && (
              <button
                type="button"
                className="btn btn-link btn-underline list-item__sizes-toggle"
                aria-label={`View ${moreFewerButtonToggleLabel}`}
                onClick={this.handleShowMoreSizesClick}>
                {moreFewerButtonToggleLabel}
              </button>
            )}
          </div>
        </div>
      </section>
    );
  }
}

export default PhotosListItem;
